import React from "react";
import { Check, X, TriangleAlert } from "lucide-react";
import {
  hexagonRoot,
  successIconClass,
  failIconClass,
  warningIconClass,
  informationIconClass,
  hexagonShape,
  iconContainer,
  iconClass,
} from "./hexagon.css";

type HexagonVariant = "success" | "fail" | "warning" | "information";

export interface HexagonProps {
  /** Visual variant (determines both colors and icon) */
  type: HexagonVariant;

  /** Optional pixel size */
  size?: number;
}

const InformationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="3"
    height="17"
    viewBox="0 0 3 17"
    fill="none"
  >
    <path
      d="M1.25 15.25V6.25M1.25 1.25H1.2575"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Variant-level config: each type has its dedicated icon and styling
 */
export const VARIANT_CONFIG: Record<
  HexagonVariant,
  {
    IconComponent: React.ComponentType<{ className?: string }>;
    iconClassName: string;
  }
> = {
  success: {
    IconComponent: Check,
    iconClassName: successIconClass,
  },
  fail: {
    IconComponent: X,
    iconClassName: failIconClass,
  },
  warning: {
    IconComponent: TriangleAlert,
    iconClassName: warningIconClass,
  },
  information: {
    IconComponent: InformationIcon,
    iconClassName: informationIconClass,
  },
};

export const Hexagon: React.FC<HexagonProps> = ({ type, size }) => {
  const { IconComponent, iconClassName } = VARIANT_CONFIG[type];

  return (
    <svg
      aria-hidden="true"
      className={`${hexagonRoot} ${iconClassName}`}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={size ? { width: size, height: size } : undefined}
    >
      <path
        d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z"
        className={hexagonShape}
      />
      <foreignObject x="6.5" y="8.5" width="15" height="15">
        <div className={iconContainer}>
          <IconComponent className={iconClass} />
        </div>
      </foreignObject>
    </svg>
  );
};